import { JupiterTheme, MockHook } from '@jupiterone/jupiter-web-toolkit';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Divider,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import { Plus, Trash } from 'react-feather';
import { Autocomplete } from '@material-ui/lab';
import PermissionTypeSelect from './PermissionTypeSelect';
import useEditPermissionDialogLogic, {
  EditPermissionDialogComponentProps,
} from './useEditPermissionDialogLogic';

const useStyles = makeStyles((theme: JupiterTheme) => {
  return createStyles({
    title: {
      color: theme.palette.primary.main,
    },
    selectContainer: {
      display: 'flex',
      alignItems: 'flex-end',
      '& > *': {
        flex: 1,
        '&:not(:first-child)': {
          marginLeft: theme.spacing(1),
        },
        '&:not(:last-child)': {
          marginRight: theme.spacing(1),
        },
      },
    },
    fieldListItemRoot: {
      display: 'block',
    },
    permissionSetListHeading: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(0.5),
    },
    fieldListItems: {
      alignItems: 'center',
      borderRadius: '4px',
      display: 'flex',
      margin: '2px 0',
      paddingLeft: '1rem',
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    fieldListItemContainer: {
      flexGrow: 1,
    },
  });
});

export const EditPermissionDialogComponent = ({
  saving,
  title,
  permissionSetValues,
  permissionTypes,
  selectedPermissionFilter,
  filterValue,
  permissionAutocomplete,
  permissionEntities,
  handlePermissionFilterChange,
  handleCancel,
  handleSave,
  handleFilterValue,
  handlePermissionValue,
  handleDeletePermissionValue,
}: EditPermissionDialogComponentProps) => {
  const classes = useStyles();

  return (
    <Dialog open={true} fullWidth maxWidth="sm">
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent>
        <Subheading>Add Filters</Subheading>
        <section className={classes.selectContainer}>
          <PermissionTypeSelect
            permissionTypes={permissionTypes}
            value={selectedPermissionFilter}
            onChange={handlePermissionFilterChange}
          />
          <Autocomplete
            freeSolo
            disableClearable
            options={permissionAutocomplete}
            inputValue={filterValue}
            onInputChange={handleFilterValue}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter value"
                margin="none"
                type="text"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {permissionEntities.loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton
                          onClick={handlePermissionValue}
                          size="small"
                          aria-label="Add to permission set"
                          disabled={!selectedPermissionFilter || !filterValue}
                        >
                          <Plus />
                        </IconButton>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </section>
        <section>
          <Subheading className={classes.permissionSetListHeading}>
            <strong>ANY</strong> of the following are true
          </Subheading>
          <List disablePadding={true}>
            <Divider />
            {!permissionSetValues.length ? (
              <ListItem divider={true} disableGutters={true}>
                No filters applied
              </ListItem>
            ) : (
              permissionSetValues.map(([field, values]) => {
                return (
                  <ListItem
                    key={field}
                    classes={{ root: classes.fieldListItemRoot }}
                    divider={true}
                    disableGutters={true}
                  >
                    <Typography>{field}</Typography>
                    {values.map((value, index) => (
                      <ValueListItem
                        key={`${field}-${value}`}
                        classes={{
                          root: classes.fieldListItems,
                          container: classes.fieldListItemContainer,
                        }}
                        deleteLabel={`Delete "${value}" from ${field} field`}
                        onDelete={handleDeletePermissionValue(field, index)}
                      >
                        {value}
                      </ValueListItem>
                    ))}
                  </ListItem>
                );
              })
            )}
          </List>
        </section>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} color="secondary" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Subheading = (props: any) => (
  <Typography variant="overline" component="h2" {...props} />
);

const ValueListItem = ({
  classes,
  children,
  deleteLabel,
  onDelete,
}: {
  classes: { root: string; container: string };
  onDelete: React.MouseEventHandler<HTMLButtonElement>;
  deleteLabel: string;
  children: React.ReactNode;
}) => (
  <div className={classes.root}>
    <div className={classes.container}>{children}</div>
    <IconButton size="small" aria-label={deleteLabel} onClick={onDelete}>
      <Trash aria-hidden="true" />
    </IconButton>
  </div>
);

export default MockHook.withHookHoc(
  EditPermissionDialogComponent,
  useEditPermissionDialogLogic
);
